import { HighlightArea } from '@react-pdf-viewer/highlight';
import { DataRoomFile } from '@server/modules/client-matter/common/types';
import { useEffect, useMemo, useState } from 'react';

import { ChevronIcon, DocumentAnalysisIcon, ListIcon } from '@/assets/icons';
import LoadingSpinner from '@/components/LoadingSpinner';
import { useDataRoom } from '@/contexts/overview/dataroom/utils';
import { ScrollableDiv } from '@/pages/overview/common/ScrollableDiv';
import { SidePanelAddTool } from '@/pages/overview/common/side-panel/SidePanelAddTool';
import { useOverview } from '@/pages/overview/common/utils';
import {
  columnVisibilityType,
  MaClausesData,
} from '@/pages/overview/contract-clauses/common/utils';
import { ResponseIconDisplay } from '@/pages/overview/contract-clauses/content/ResponseIconDisplay';
import { trpcReact } from '@/utils/trpc';

interface DocumentAnalysisTabProps {
  pdfInteraction: boolean;
  hoveredHighlight?: HighlightArea | null;
  handleJumpToHighlight?: (highlights: number[]) => void;
}

export const DocumentAnalysisTab = ({
  pdfInteraction,
  hoveredHighlight,
  handleJumpToHighlight,
}: DocumentAnalysisTabProps) => {
  const [documentAnalysisData, setDocumentAnalysisData] = useState<MaClausesData[]>([]);
  const [isAddingDocumentAnalysisTool, setIsAddingDocumentAnalysisTool] = useState(false);
  const { matter, checkForTool, getToolProcessing } = useOverview();
  const { selectedFile } = useDataRoom();
  const addToolFunction = trpcReact.clientMatter.addTool.useMutation();

  const handleAddDocumentAnalysisTool = () => {
    setIsAddingDocumentAnalysisTool(true);
    addToolFunction.mutate(
      {
        clientNumber: matter.client.number,
        number: matter.number,
        tool: 'CONTRACT_CLAUSES',
        clientMatterId: matter.id,
      },
      {
        onSettled: () => {
          setIsAddingDocumentAnalysisTool(false);
        },
      },
    );
  };

  const getResponseColor = (response?: string, summary?: string) => {
    if (!response) return 'marveri-white';
    if (response === 'Yes') return '#f44144'; // red
    if (response === 'Explicitly Permitted') return '#78BB79'; // green
    if ((response === 'No' || response.startsWith('No - ')) && summary) return '#78BB79'; // green
    if (response === 'Further Review Required') return '#FFCC00'; // amber
    return 'marveri-white';
  };

  const documentAnalysisToolProcessing = useMemo(() => {
    return getToolProcessing('CONTRACT_CLAUSES');
  }, [getToolProcessing]);

  const columnCategoryToTitleMap: Record<string, string> = useMemo(
    () => ({
      'file name': 'File Name',
      description: 'Description',
      agreement_name: 'Document Name',
      parties: 'Parties',
      agreement_date: 'Document Date',
      effective_date: 'Effective Date',
      expiration_date: 'Expiration Date',
      renewal_term: 'Renewal Period',
      notice_to_terminate_renewal: 'Notice to Terminate Renewal',
      termination_for_convenience: 'Termination for Convenience',
      anti_assignment: 'Anti-Assignment',
      change_of_control: 'Change of Control',
      non_compete: 'Non-Compete',
      exclusivity: 'Exclusivity',
      no_solicit_of_customers: 'Non-Solicit of Customers',
      rofo_rofr_rofn: 'ROFO/ ROFR/ ROFN',
      most_favored_nation: 'Most Favored Nation',
      governing_law: 'Governing Law',
    }),
    [],
  );

  const localStorageTabKey = `tabVisibility${matter.id}`;

  /** Titles (not backend keys) in the same order the ColumnSelector shows them */
  const selectorColumnTitles = useMemo(() => {
    if (!matter.requestedDocumentAnalysisColumns) return [];
    return matter.requestedDocumentAnalysisColumns
      .map((key) => columnCategoryToTitleMap[key] || key) // fall-back for customs
      .filter(Boolean);
  }, [matter.requestedDocumentAnalysisColumns, columnCategoryToTitleMap]);

  /** Resolve a human-readable title for any clause (built-ins or custom) */
  const getClauseTitle = (clause: { category: string; customName?: string; label?: string }) =>
    columnCategoryToTitleMap[clause.category] ||
    clause.customName ||
    clause.label ||
    clause.category;

  const getInitialTabVisibility = () => {
    const storedTabs = localStorage.getItem(localStorageTabKey);
    if (storedTabs) {
      return JSON.parse(storedTabs);
    }
    return Object.fromEntries(
      Object.values(columnCategoryToTitleMap).map((title) => [title, false]),
    );
  };

  const [tabVisibility, setTabVisibility] = useState<columnVisibilityType>(getInitialTabVisibility);

  useEffect(() => {
    if (selectedFile && documentAnalysisToolProcessing.complete) {
      const selectedFileClauseData = selectedFile.maClausesData.map((clause) => {
        return {
          id: clause.id,
          sourceDocument: selectedFile as DataRoomFile,
          maClauses: clause.maClauses.map((clause) => {
            const firstCitation = clause.citations?.[0];
            return {
              ...clause,
              citation: firstCitation?.key || null,
              citations: clause.citations || [],
              locations: firstCitation?.locations || [],
              summary: clause.summary,
              userResponse: clause.userResponse || '',
              userSummary: clause.userSummary || '',
              clauseId: clause.id,
              customContractClauseDefinitionId: clause.customContractClauseDefinitionId,
              completed:
                selectedFile.customContractClauseCompletionStatus.find(
                  (status) =>
                    status.customContractClauseDefinitionId ===
                    clause.customContractClauseDefinitionId,
                )?.completed || false,
            };
          }),
        };
      });
      setDocumentAnalysisData(selectedFileClauseData);
    }
  }, [matter.dataRoomFiles, selectedFile, documentAnalysisToolProcessing.complete]);

  const handleTabVisibilityChange = (category: string) => {
    setTabVisibility((prev) => {
      const newTabVisibility = { ...prev };
      newTabVisibility[category] = !newTabVisibility[category];
      localStorage.setItem(localStorageTabKey, JSON.stringify(newTabVisibility));
      return newTabVisibility;
    });
  };

  const handleCitationClick = (highlight: number[] | undefined) => {
    if (pdfInteraction && highlight && handleJumpToHighlight) {
      handleJumpToHighlight(highlight);
    }

    return;
  };

  return (
    <ScrollableDiv
      containerStyle={`mt-[18px] flex flex-col ${
        pdfInteraction ? 'h-[calc(100vh-170px)]' : 'h-[calc(100vh-140px)]'
      } pr-2 scrollbar-thumb-[#403E40]`}
      scrollKey="document-analysis-tab"
    >
      {checkForTool('CONTRACT_CLAUSES') ? (
        documentAnalysisToolProcessing.complete ? (
          <div>
            {documentAnalysisData.map((data) => (
              <div key={data.id} data-testid="document-analysis-tab-section">
                <div className="flex flex-col gap-2">
                  {data.maClauses
                    /* keep only columns shown in selector & honour its order */
                    .filter((cl) => selectorColumnTitles.includes(getClauseTitle(cl)))
                    .sort(
                      (a, b) =>
                        selectorColumnTitles.indexOf(getClauseTitle(a)) -
                        selectorColumnTitles.indexOf(getClauseTitle(b)),
                    )
                    .map((clause) => {
                      const title = getClauseTitle(clause);

                      const isClauseHighlighted = clause.citations?.some(
                        (citation) =>
                          citation.locations?.[0] === hoveredHighlight?.pageIndex &&
                          citation.locations?.[2] === hoveredHighlight?.top,
                      );

                      return (
                        <div key={clause.clauseId}>
                          <div
                            className={`flex h-[44px] w-full cursor-pointer items-center gap-2 rounded-[6px] p-3 transition-colors ${
                              isClauseHighlighted ? 'bg-[#565256]' : 'bg-[#353336]'
                            }`}
                            onClick={() => handleTabVisibilityChange(title)}
                          >
                            {/* Left: icon + title */}
                            <div className="flex items-center gap-2">
                              {[
                                'yes',
                                'no',
                                'explicitly permitted',
                                'further review required',
                              ].some(
                                (clauseIndicator) =>
                                  clause.response?.toLowerCase().startsWith(clauseIndicator) &&
                                  clause.summary,
                              ) ? (
                                <ResponseIconDisplay
                                  response={clause.response}
                                  tabDisplay={true}
                                  showExplanations={false}
                                />
                              ) : (
                                <img src={ListIcon} alt="Clause" className="size-[18px]" />
                              )}
                              <span>{title}</span>
                            </div>

                            {/* Right: Chevron icon */}
                            <img
                              src={ChevronIcon}
                              className={`${tabVisibility[title] ? 'rotate-180' : ''} transition-transform duration-200`}
                              alt="Toggle"
                            />
                          </div>
                          {tabVisibility[title] && (
                            <div className="p-3">
                              <span
                                className="text-[14px] font-normal"
                                style={{ color: getResponseColor(clause.response, clause.summary) }}
                              >
                                {clause.response}
                              </span>
                              {/* EXPLANATION */}
                              {(clause.summary || clause.userSummary) && (
                                <div>
                                  <span className="block text-[13px] font-normal text-N300">
                                    {clause.userSummary || clause.summary}
                                  </span>
                                </div>
                              )}
                              {clause.citation && (
                                <div className="flex flex-col">
                                  <h2 className="my-2 text-[14px] font-medium text-N400">
                                    Citations
                                  </h2>
                                  {clause.citations.map((citation) => {
                                    return (
                                      <div
                                        key={citation.key}
                                        className={`${pdfInteraction ? 'cursor-pointer' : 'cursor-default'} ${hoveredHighlight?.pageIndex === citation.locations?.[0] && hoveredHighlight?.top === citation.locations?.[2] ? 'bg-N500' : 'bg-N600'} mb-2 rounded-[8px] py-3 pl-3 pr-6 text-[14px] font-normal`}
                                        onClick={() => {
                                          handleCitationClick(citation.locations);
                                        }}
                                      >
                                        {`"${citation.key}"`}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2">
            <LoadingSpinner />
            <span>Analyzing Document...</span>
          </div>
        )
      ) : (
        <SidePanelAddTool
          toolName="Document Analysis"
          toolIcon={DocumentAnalysisIcon}
          toolLoaderText="Adding Document Analysis..."
          isButtonDisabled={isAddingDocumentAnalysisTool}
          onClick={handleAddDocumentAnalysisTool}
        />
      )}
    </ScrollableDiv>
  );
};
